import type { FaelligkeitsStufe, TeilnahmeAntwort, UnterweisungFrage } from './types';

/**
 * Regeln der Teilnehmerseite und der Nachweise — reine Funktionen, keine
 * Datenbank, kein DOM (docs/sifa-unterweisung-plan.md §5, §7).
 *
 * WAS HIER STEHT, GILT NUR ZUR ANZEIGE: die Fragen werden beim Abschluss in
 * der Datenbank ausgewertet (`sifa_teilnahme_abschliessen`), denn die
 * Richtig-Flags erreichen den Teilnehmer nie. `bewerteAntworten` rechnet mit
 * DERSELBEN Regel — sie dient dem Innendienst als Vorschau im Editor und den
 * Tests; wer sie aendert, aendert die SQL-Fassung mit.
 *
 * DATUMSRECHNUNG PER ZEICHENKETTE: `gueltigBis` und `faelligkeitsStufe`
 * zerlegen `YYYY-MM-DD` selbst und rechnen ueber `Date.UTC`. Ein reines
 * Tagesdatum durch `new Date(...)` zu schicken hiesse, es als UTC-Mitternacht
 * zu lesen — westlich von Greenwich staende dann der Vortag auf dem
 * Zertifikat (dieselbe Falle wie in einweisungBooking.formatiereDatum).
 */

// ─── Formular ───────────────────────────────────────────────────────────────

export interface TeilnahmeFormular {
  vorname?: string | null;
  nachname?: string | null;
  /** ISO `YYYY-MM-DD` (Wert eines `<input type="date">`). */
  geburtsdatum?: string | null;
  email?: string | null;
}

export type FormularFeld = keyof TeilnahmeFormular;

export interface FormularFehler {
  feld: FormularFeld;
  text: string;
}

/** Juengstes zulaessiges Alter — unter 14 gibt es keine Beschaeftigten, die zu unterweisen waeren. */
export const MINDEST_ALTER = 14;
/** Aeltestes plausibles Alter — darueber ist es ein Tippfehler im Jahr. */
export const HOECHST_ALTER = 100;

/**
 * Formale Pruefung einer E-Mail-Adresse: etwas, ein @, etwas, ein Punkt,
 * etwas. Bewusst grob — die Adresse wird nicht zugestellt geprueft, und ein
 * strengerer Ausdruck lehnt gueltige Adressen ab. Leerzeichen sind nie
 * zulaessig.
 */
export const istEmailForm = (wert?: string | null): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((wert ?? '').trim());

/** `YYYY-MM-DD` streng: vier-zwei-zwei Ziffern UND ein Kalendertag, der existiert. */
export const istIsoTag = (wert?: string | null): boolean => {
  const teile = zerlegeTag(wert);
  if (!teile) return false;
  const [j, m, t] = teile;
  if (m < 1 || m > 12 || t < 1) return false;
  return t <= tageImMonat(j, m);
};

/** Der heutige Tag als `YYYY-MM-DD` in der Ortszeit des Geraets. */
export const heuteIso = (jetzt: Date = new Date()): string => {
  const j = jetzt.getFullYear();
  const m = String(jetzt.getMonth() + 1).padStart(2, '0');
  const t = String(jetzt.getDate()).padStart(2, '0');
  return `${j}-${m}-${t}`;
};

/**
 * Volle Lebensjahre am Stichtag. `null`, wenn eines der Daten unbrauchbar
 * ist. Rein ueber die Ziffern: Jahr minus Jahr, minus eins, falls der
 * Geburtstag im Stichtagsjahr noch nicht war.
 */
export const alterInJahren = (geburtsdatum?: string | null, stichtag?: string | null): number | null => {
  const g = zerlegeTag(geburtsdatum);
  const s = zerlegeTag(stichtag);
  if (!g || !s) return null;
  let alter = s[0] - g[0];
  if (s[1] < g[1] || (s[1] === g[1] && s[2] < g[2])) alter--;
  return alter;
};

/**
 * Prueft das Abschlussformular des Teilnehmers.
 *
 * Rueckgabe: Liste der Fehler mit Feld und deutschem Text — leer heisst
 * gueltig. Je Feld hoechstens ein Fehler, der erste Grund gewinnt (ein
 * leeres Feld meldet „Pflicht", nicht auch noch „Form").
 *
 * `heute` ist ein Parameter, damit die Regel ohne Uhr pruefbar ist; die
 * Oberflaeche laesst ihn weg.
 */
export const pruefeFormular = (
  formular: TeilnahmeFormular | null | undefined,
  heute: string = heuteIso(),
): FormularFehler[] => {
  const f = formular ?? {};
  const fehler: FormularFehler[] = [];

  if (!(f.vorname ?? '').trim()) fehler.push({ feld: 'vorname', text: 'Bitte den Vornamen eingeben.' });
  if (!(f.nachname ?? '').trim()) fehler.push({ feld: 'nachname', text: 'Bitte den Nachnamen eingeben.' });

  const geb = (f.geburtsdatum ?? '').trim();
  if (!geb) {
    fehler.push({ feld: 'geburtsdatum', text: 'Bitte das Geburtsdatum eingeben.' });
  } else if (!istIsoTag(geb)) {
    fehler.push({ feld: 'geburtsdatum', text: 'Das Geburtsdatum ist kein gültiges Datum.' });
  } else if (geb > heute) {
    fehler.push({ feld: 'geburtsdatum', text: 'Das Geburtsdatum darf nicht in der Zukunft liegen.' });
  } else {
    const alter = alterInJahren(geb, heute);
    if (alter !== null && alter < MINDEST_ALTER) {
      fehler.push({ feld: 'geburtsdatum', text: `Teilnehmer müssen mindestens ${MINDEST_ALTER} Jahre alt sein.` });
    } else if (alter !== null && alter > HOECHST_ALTER) {
      fehler.push({ feld: 'geburtsdatum', text: 'Bitte das Geburtsjahr prüfen.' });
    }
  }

  const email = (f.email ?? '').trim();
  if (!email) {
    fehler.push({ feld: 'email', text: 'Bitte die E-Mail-Adresse eingeben — dorthin geht das Zertifikat.' });
  } else if (!istEmailForm(email)) {
    fehler.push({ feld: 'email', text: 'Die E-Mail-Adresse ist nicht vollständig (z. B. name@firma.de).' });
  }

  return fehler;
};

// ─── Lernerfolgskontrolle ───────────────────────────────────────────────────

export interface Bewertung {
  punkte: number;
  punkteMax: number;
  /** Erreichte Punkte in Prozent (0..100); 100 bei null Fragen. */
  prozent: number;
  bestanden: boolean;
}

/**
 * Wertet die Antworten aus — DIESELBE Regel wie `sifa_teilnahme_abschliessen`.
 *
 * Eine Frage gibt genau 1 Punkt, wenn die gewaehlte Menge der Antwort-Ids
 * GLEICH der Menge der richtigen Ids ist: eine fehlende richtige oder eine
 * zusaetzlich falsche Antwort ist 0 Punkte, keine halben. Eine Frage ohne
 * markierte richtige Antwort (Anlagefehler) ist nur mit leerer Auswahl
 * richtig. Fragen ohne Antwort des Teilnehmers zaehlen 0.
 *
 * Bestanden = punkte/punkteMax >= bestehenProzent/100, ganzzahlig gerechnet
 * (`punkte * 100 >= bestehenProzent * punkteMax`), damit 4 von 5 bei 80 %
 * nicht an einer Gleitkommastelle scheitert. NULL Fragen = bestanden: eine
 * Unterweisung ohne Lernerfolgskontrolle ist mit dem Lesen erledigt.
 *
 * Nur die Fragen zaehlen, die es im Kurs GIBT: Antworten auf unbekannte
 * Frage-Ids werden ignoriert, statt punkteMax zu verschieben.
 */
export const bewerteAntworten = (
  fragen: ReadonlyArray<UnterweisungFrage> | null | undefined,
  antworten: ReadonlyArray<TeilnahmeAntwort> | null | undefined,
  bestehenProzent: number,
): Bewertung => {
  const liste = fragen ?? [];
  const gewaehltJeFrage = new Map<string, Set<string>>();
  for (const a of antworten ?? []) {
    if (!a?.frageId) continue;
    gewaehltJeFrage.set(a.frageId, new Set((a.antwortIds ?? []).filter(Boolean)));
  }

  let punkte = 0;
  for (const frage of liste) {
    const richtig = new Set((frage.antworten ?? []).filter((x) => x.richtig === true).map((x) => x.id));
    const gewaehlt = gewaehltJeFrage.get(frage.id) ?? new Set<string>();
    if (mengenGleich(richtig, gewaehlt)) punkte++;
  }

  const punkteMax = liste.length;
  const grenze = Number.isFinite(bestehenProzent) ? Math.min(100, Math.max(0, bestehenProzent)) : 100;
  const bestanden = punkteMax === 0 || punkte * 100 >= grenze * punkteMax;
  const prozent = punkteMax === 0 ? 100 : Math.round((punkte * 100) / punkteMax);
  return { punkte, punkteMax, prozent, bestanden };
};

const mengenGleich = (a: Set<string>, b: Set<string>): boolean => {
  if (a.size !== b.size) return false;
  for (const x of a) if (!b.has(x)) return false;
  return true;
};

/** Frage-Ids, die der Teilnehmer noch nicht (oder leer) beantwortet hat. */
export const offeneFragen = (
  fragen: ReadonlyArray<UnterweisungFrage> | null | undefined,
  antworten: ReadonlyArray<TeilnahmeAntwort> | null | undefined,
): string[] => {
  const beantwortet = new Set(
    (antworten ?? []).filter((a) => a?.frageId && (a.antwortIds ?? []).length > 0).map((a) => a.frageId),
  );
  return (fragen ?? []).map((f) => f.id).filter((id) => !beantwortet.has(id));
};

// ─── Fortschritt und Lesen ──────────────────────────────────────────────────

/** Fortschritt in Prozent (0..100), ganzzahlig. `schritt` = erledigte Schritte. */
export const fortschritt = (schritt: number, gesamt: number): number => {
  if (!Number.isFinite(gesamt) || gesamt <= 0) return 0;
  const s = Number.isFinite(schritt) ? Math.min(gesamt, Math.max(0, schritt)) : 0;
  return Math.round((s * 100) / gesamt);
};

/** Lesegeschwindigkeit, mit der die Mindestlesezeit geschaetzt wird. */
export const WOERTER_PRO_MINUTE = 200;
/** Unter zwei Sekunden ist „Weiter" sofort da — ein leerer Abschnitt sperrt nicht. */
export const MINDEST_LESEZEIT_MS_MIN = 2_000;
/** Mehr als anderthalb Minuten sperrt keinen Abschnitt — Scroll-Ende schaltet ohnehin frei. */
export const MINDEST_LESEZEIT_MS_MAX = 90_000;

/**
 * Wie lange „Weiter" nach dem Aufblenden eines Abschnitts gesperrt bleibt,
 * geschaetzt aus dem Text: Woerter / 200 pro Minute, begrenzt auf 2..90 s.
 */
export const mindestLesezeitMs = (text?: string | null): number => {
  const woerter = (text ?? '').trim().split(/\s+/).filter(Boolean).length;
  const ms = Math.round((woerter / WOERTER_PRO_MINUTE) * 60_000);
  return Math.min(MINDEST_LESEZEIT_MS_MAX, Math.max(MINDEST_LESEZEIT_MS_MIN, ms));
};

/**
 * Darf der Teilnehmer den Abschnitt verlassen? Ja nach Mindestlesezeit ODER
 * wenn er bis ans Ende gescrollt hat (§5: „Weiter erst nach Mindestlesezeit
 * oder Scroll-Ende").
 */
export const istVollstaendigGelesen = (gelesenMs: number, mindestMs: number, scrollEnde: boolean): boolean =>
  scrollEnde === true || (Number.isFinite(gelesenMs) && gelesenMs >= mindestMs);

// ─── Zertifikat-Nr. und Gueltigkeit ─────────────────────────────────────────

/**
 * Naechste Zertifikat-Nr. `UW-<projektnummer>-<jahr>-<lfd>`.
 *
 * Die laufende Nummer zaehlt je Projekt UND Jahr, ohne Auffuellung (UW-6051-
 * 2026-1, …-2, …-10). Aus `vorhandene` zaehlen nur Nummern mit demselben
 * Praefix; fremde oder unlesbare Eintraege stoeren nicht. Lueckenfuellung
 * findet nicht statt: hoechste + 1, wie bei allen laufenden Nummern im
 * Bestand.
 *
 * Diese Vorschau ist nicht der Riegel — der ist der Unique-Index auf
 * `zertifikat_nr`, und die Nummer vergibt die RPC beim Abschluss.
 */
export const naechsteZertifikatNr = (
  projektnummer: string,
  jahr: number | string,
  vorhandene: ReadonlyArray<string | null | undefined> | null | undefined,
): string => {
  const praefix = `UW-${(projektnummer ?? '').trim()}-${String(jahr).trim()}-`;
  let hoechste = 0;
  for (const nr of vorhandene ?? []) {
    const text = (nr ?? '').trim();
    if (!text.startsWith(praefix)) continue;
    const rest = text.slice(praefix.length);
    if (!/^\d+$/.test(rest)) continue;
    hoechste = Math.max(hoechste, Number(rest));
  }
  return `${praefix}${hoechste + 1}`;
};

/**
 * Ablaufdatum eines Nachweises: Abschlusstag + `gueltigMonate`, als
 * `YYYY-MM-DD`. `null` bei 0 Monaten (ohne Ablauf) oder unbrauchbarem Datum.
 *
 * Monatsaddition ueber die Ziffern: der Tag bleibt, das Monatsende wird
 * abgeschnitten (31.01. + 1 Monat = 28./29.02.). Kein `setMonth`, das bei
 * Ueberlauf still in den Folgemonat rutscht.
 */
export const gueltigBis = (abgeschlossenAmIso?: string | null, gueltigMonate?: number | null): string | null => {
  const monate = Number(gueltigMonate);
  if (!Number.isFinite(monate) || monate <= 0) return null;
  const teile = zerlegeTag(abgeschlossenAmIso);
  if (!teile) return null;
  const [j, m, t] = teile;
  const gesamt = m - 1 + Math.trunc(monate);
  const jahr = j + Math.floor(gesamt / 12);
  const monat = (gesamt % 12) + 1;
  const tag = Math.min(t, tageImMonat(jahr, monat));
  return `${jahr}-${String(monat).padStart(2, '0')}-${String(tag).padStart(2, '0')}`;
};

/** Vorwarnzeit der Faelligkeitsampel: gelb ab 60 Tagen vor Ablauf. */
export const WARN_TAGE = 60;

/**
 * Ampel eines Nachweises am Stichtag: `abgelaufen` (rot), `bald` (gelb, in
 * hoechstens `warnTage`), `gueltig` (gruen), `ohne` (kein Ablauf oder kein
 * Datum). Der Ablauftag selbst ist noch gueltig; der Tag danach abgelaufen.
 */
export const faelligkeitsStufe = (
  gueltigBisIso?: string | null,
  heute: string = heuteIso(),
  warnTage: number = WARN_TAGE,
): FaelligkeitsStufe => {
  const tage = tageBis(gueltigBisIso, heute);
  if (tage === null) return 'ohne';
  if (tage < 0) return 'abgelaufen';
  if (tage <= warnTage) return 'bald';
  return 'gueltig';
};

/** Tage von `heute` bis `zielIso` (negativ = vorbei); `null` bei unbrauchbarem Datum. */
export const tageBis = (zielIso?: string | null, heute: string = heuteIso()): number | null => {
  const z = zerlegeTag(zielIso);
  const h = zerlegeTag(heute);
  if (!z || !h) return null;
  const diff = Date.UTC(z[0], z[1] - 1, z[2]) - Date.UTC(h[0], h[1] - 1, h[2]);
  return Math.round(diff / 86_400_000);
};

// ─── Datumshelfer ───────────────────────────────────────────────────────────

/** `YYYY-MM-DD` (auch mit Zeitanteil) → [Jahr, Monat, Tag]; sonst null. */
const zerlegeTag = (wert?: string | null): [number, number, number] | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:$|T|\s)/.exec((wert ?? '').trim());
  if (!m) return null;
  return [Number(m[1]), Number(m[2]), Number(m[3])];
};

const tageImMonat = (jahr: number, monat: number): number => new Date(Date.UTC(jahr, monat, 0)).getUTCDate();
